/* splashPlan — pure splash-duration policy + dismissal scheduler.
   Extracted from SplashMaze so the node vitest environment (no jsdom)
   can unit-test the plan matrix and the timer choreography. */

import { isMobileViewport } from '@/lib/useIsMobile';

export const SPLASH_SEEN_KEY = 'daedalus.splashSeen';

export interface SplashPlan {
  minMs: number;   // never dismiss before this (brand moment)
  maxMs: number;   // ALWAYS dismiss by this (ceiling — today's durations)
  quick: boolean;  // static finished frame instead of the draw choreography
}

export function computeSplashPlan(opts: {
  isMobile: boolean;
  reducedMotion: boolean;
  seenThisSession: boolean;
}): SplashPlan {
  const ceiling = opts.isMobile ? 2000 : 6500;
  if (opts.reducedMotion) return { minMs: 400, maxMs: 800, quick: true };
  if (opts.seenThisSession) return { minMs: 800, maxMs: ceiling, quick: true };
  return { minMs: opts.isMobile ? 1200 : 2600, maxMs: ceiling, quick: false };
}

/** Browser-facing wrapper: reads media queries + sessionStorage, marks seen. */
export function readSplashPlan(): SplashPlan {
  let seen = false;
  try {
    seen = sessionStorage.getItem(SPLASH_SEEN_KEY) === '1';
    sessionStorage.setItem(SPLASH_SEEN_KEY, '1'); // mount-time, not finish-time
  } catch { /* storage unavailable → treat as first load */ }
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return computeSplashPlan({ isMobile: isMobileViewport(), reducedMotion: reduced, seenThisSession: seen });
}

/* --- dismissal scheduler, extracted pure for fake-timer tests --- */

export function createSplashScheduler(opts: {
  minMs: number;
  maxMs: number;
  onFinish: () => void;               // idempotence is the caller's firedRef
  onAppReady: (fn: () => void) => () => void;  // injected → bootSignal.onAppReady
  now?: () => number;                 // injected → performance.now
}): () => void /* dispose */ {
  const now = opts.now ?? (() => performance.now());
  const start = now();
  const ceiling = setTimeout(opts.onFinish, opts.maxMs);
  let minHold: ReturnType<typeof setTimeout> | undefined;
  const off = opts.onAppReady(() => {
    const elapsed = now() - start;
    if (elapsed >= opts.minMs) opts.onFinish();
    else minHold = setTimeout(opts.onFinish, opts.minMs - elapsed);
  });
  return () => {
    clearTimeout(ceiling);
    if (minHold !== undefined) clearTimeout(minHold);
    off();
  };
}
